import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAudioOn as setSharedAudioOn } from '@/shared/app';

import type { GameAudioChangeEvent, GameAudioProps } from '@/features/gameSession/types';

type GameAudioContextValue = {
	audioOn: boolean;
	onAudioChange?: (event: GameAudioChangeEvent) => void;
};

const GameAudioContext = createContext<GameAudioContextValue | null>(null);

const selectStoreAudioOn = (state: any) =>
	state?.sharedReducer?.userData?.settings?.audio_on ?? state?.sharedReducer?.audioOn ?? true;

type GameAudioProviderProps = GameAudioProps & {
	children: ReactNode;
};

export const GameAudioProvider = ({ audioOn, onAudioChange, children }: GameAudioProviderProps) => {
	const value = useMemo(() => {
		if (audioOn === undefined) {
			return null;
		}

		return {
			audioOn,
			onAudioChange,
		};
	}, [audioOn, onAudioChange]);

	if (!value) {
		return children;
	}

	return <GameAudioContext.Provider value={value}>{children}</GameAudioContext.Provider>;
};

export const useResolvedGameAudioOn = () => {
	const gameAudio = useContext(GameAudioContext);
	const storeAudioOn = useSelector(selectStoreAudioOn);

	return gameAudio?.audioOn ?? storeAudioOn;
};

export const useGameAudioController = () => {
	const dispatch = useDispatch<any>();
	const gameAudio = useContext(GameAudioContext);
	const audioOn = useResolvedGameAudioOn();

	const setAudioOn = useCallback(
		(nextAudioOn: boolean) => {
			if (gameAudio) {
				gameAudio.onAudioChange?.({
					audioOn: nextAudioOn,
				});
				return;
			}

			dispatch(setSharedAudioOn(nextAudioOn));
		},
		[dispatch, gameAudio],
	);

	return {
		audioOn,
		setAudioOn,
		isControlled: Boolean(gameAudio),
	};
};
